/**
 * P10 Coach drawer frame (screens §P10). The frame owns open/close, the context chip, focus handling and the two standing
 * lines; WP28 fills `thread` (concept card, streamed replies, hints, starter questions).
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useEffect } from "react";
import { useLocation } from "../shell/history";
import { Drawer } from "../theme/Drawer";

/** What the viewer was looking at when they opened the Coach: a screen, optionally a decision or a concept card. */
export interface CoachContextRef {
  readonly screen: string;
  readonly label: string;
  readonly decisionId?: string;
  readonly conceptId?: string;
  /** A question to ask the moment the thread opens ("Explain this number"). */
  readonly ask?: string;
  /** The deterministic first answer to `ask` (no AI): what the screen already knows. Without it the question follows the same routing as any other. */
  readonly answer?: string;
  /** Measures on this screen with a deterministic explanation, by KPI id: a "why did X move" question about one is answered from here. */
  readonly explain?: Readonly<Record<string, string>>;
  /** The explanation of the result on screen ("why am I behind my twin"): used in place of the latest released round's. */
  readonly results?: string;
}

/** `opens` counts how many times the drawer has been opened: a floating card on the page (a term popover) closes when it changes. */
interface CoachControl { readonly open: (context: CoachContextRef) => void; readonly close: () => void; readonly opens: number }

const CoachContext = createContext<CoachControl>({ open: () => undefined, close: () => undefined, opens: 0 });
export const useCoach = (): CoachControl => useContext(CoachContext);

export function CoachProvider({ thread, children }: { readonly thread?: (context: CoachContextRef) => ReactNode; readonly children: ReactNode }) {
  const [context, setContext] = useState<CoachContextRef | null>(null);
  const close = useCallback(() => setContext(null), []);
  const [opens, setOpens] = useState(0);
  const control = useMemo<CoachControl>(() => ({ open: (c) => { setContext(c); setOpens((n) => n + 1); }, close, opens }), [close, opens]);
  return (
    <CoachContext value={control}>
      {children}
      {context === null ? null : <CoachDrawer context={context} onClose={close}>{thread?.(context)}</CoachDrawer>}
    </CoachContext>
  );
}

/** What the chip says the Coach is looking at: the caller's own label, or, for the generic one, the screen the path names ("Round 2 results"). */
export function lookingAt(pathname: string, label: string): string {
  if (label !== "This screen") return label;
  const results = /\/results\/(\d+)/.exec(pathname);
  if (results !== null) return `Round ${results[1] ?? ""} results`;
  const names: readonly (readonly [string, string])[] = [["/cockpit", "Cockpit"], ["/decide", "Decide"], ["/outlook", "Market outlook"], ["/financials", "Bank"], ["/chair", "Take the chair"], ["/solo/", "Your run"]];
  return names.find(([part]) => pathname.includes(part))?.[1] ?? label;
}

export function CoachDrawer({ context, onClose, children }: { readonly context: CoachContextRef; readonly onClose: () => void; readonly children?: ReactNode }) {
  const { pathname } = useLocation();
  // The page behind stays where it is: no second scrollbar while the drawer is open.
  useEffect(() => { const el = document.documentElement; const before = el.style.overflow; el.style.overflow = "hidden"; return () => { el.style.overflow = before; }; }, []);
  return (
    <div data-testid="coach.drawer" data-context-screen={context.screen} {...(context.decisionId === undefined ? {} : { "data-context-decision": context.decisionId })}>
      <Drawer
        title="Coach"
        onClose={onClose}
        context={<span className="gx-badge gx-badge--neutral" data-testid="coach.context">Looking at: {lookingAt(pathname, context.label)}</span>}
      >
        {children ?? <p className="gx-muted">Ask about a term, a decision or last year&apos;s result. The concept cards work even when the Coach is busy.</p>}
      </Drawer>
    </div>
  );
}
